import { c as _c } from "react/compiler-runtime";
import clsx from "classnames";
import { ContentSummaryTag } from "common/components/ContentSummaryTag";
import { Heading } from "common/components/Heading";
import { ImgixImage } from "common/components/ImgixImage";
import { closestCrop } from "modules/editor/utils/closestCrop";
import { ImageAspectRatio } from "modules/editor/types";
import type { ContentSummaryVariantProps } from "../types";
import { Image, Maybe } from "common/types/graphql";
interface BaseWideSummaryImageProps {
  image?: Maybe<Partial<Image>>;
  title?: string;
  imageSizes?: string;
  imageFit?: "cover" | "contain";
}

/**
 * `BaseWideSummaryImage` is a functional component that renders an image with different crops based on the screen size.
 * For larger screens (2xl), a Pano crop is used, and for smaller screens, a square crop is used.
 */
export const BaseWideSummaryImage = t0 => {
  "use memo";

  const $ = _c(23);
  const {
    image,
    imageSizes,
    imageFit
  } = t0;
  if (image?.src === undefined) {
    return null;
  }
  let t1;
  if ($[0] !== image.crops) {
    t1 = image.crops || [];
    $[0] = image.crops;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = closestCrop(ImageAspectRatio.Square, t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const squareCrop = t2;
  let t3;
  if ($[4] !== image.crops) {
    t3 = image.crops || [];
    $[4] = image.crops;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = closestCrop(ImageAspectRatio.Pano, t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const panoCrop = t4;
  let t5;
  if ($[8] !== image || $[9] !== imageFit || $[10] !== imageSizes) {
    t5 = {
      ...image,
      src: image.src,
      sizes: imageSizes,
      objectFit: imageFit
    };
    $[8] = image;
    $[9] = imageFit;
    $[10] = imageSizes;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const imageProps = t5;
  let t6;
  if ($[12] !== image.alt || $[13] !== imageProps || $[14] !== panoCrop) {
    t6 = <div className="relative col-span-1 hidden aspect-2/1 sm:col-span-2 2xl:block"><ImgixImage {...imageProps} crop={panoCrop} alt={image.alt} /></div>;
    $[12] = image.alt;
    $[13] = imageProps;
    $[14] = panoCrop;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== image.alt || $[17] !== imageProps || $[18] !== squareCrop) {
    t7 = <div className="relative col-span-1 aspect-square sm:col-span-2 2xl:hidden"><ImgixImage {...imageProps} crop={squareCrop} alt={image.alt} /></div>;
    $[16] = image.alt;
    $[17] = imageProps;
    $[18] = squareCrop;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};

// Basewide no longer supports overriding the image aspect ratio
export type BaseWideVariantProps = Omit<ContentSummaryVariantProps, "desktopImageAspectRatio">;
export const BaseWide = t0 => {
  "use memo";

  const $ = _c(25);
  const {
    title,
    url,
    byline,
    hideByline,
    contentType,
    imageSizes,
    image,
    imageFit,
    tagLabel,
    tagStyle,
    anchorTarget,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = clsx("group grid h-fit w-full grid-cols-3 gap-x-4 gap-y-2 outline-offset-4 sm:grid-cols-6 2xl:gap-x-8", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = title || "";
  let t3;
  if ($[2] !== t2) {
    t3 = <span className="group-hover:text-indigo-600 group-hover:underline"><Heading level={3} styledAsLevel={5}><span dangerouslySetInnerHTML={{
          __html: t2
        }} /></Heading></span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== contentType || $[5] !== tagLabel || $[6] !== tagStyle) {
    t4 = <ContentSummaryTag contentType={contentType} tagLabel={tagLabel} tagStyle={tagStyle} />;
    $[4] = contentType;
    $[5] = tagLabel;
    $[6] = tagStyle;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== byline || $[9] !== hideByline) {
    t5 = byline && !hideByline && <p className="text-3xs line-clamp-3 text-gray-600">{byline}</p>;
    $[8] = byline;
    $[9] = hideByline;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <section className="col-span-2 gap-2 sm:col-span-4">{t3}{t4}{t5}</section>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== image || $[16] !== imageFit || $[17] !== imageSizes) {
    t7 = <BaseWideSummaryImage image={image} imageSizes={imageSizes} imageFit={imageFit} />;
    $[15] = image;
    $[16] = imageFit;
    $[17] = imageSizes;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== anchorTarget || $[20] !== t1 || $[21] !== t6 || $[22] !== t7 || $[23] !== url) {
    t8 = <article className="contents"><a href={url} target={anchorTarget} className={t1}>{t6}{t7}</a></article>;
    $[19] = anchorTarget;
    $[20] = t1;
    $[21] = t6;
    $[22] = t7;
    $[23] = url;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};